'use client';

import React from 'react';
import { Quote } from 'lucide-react';

import { useLocale } from '@/lib/i18n';

export interface TestimonialItem {
  quote: string;
  author: string;
}

/**
 * Customer quotes shown on the home page. Renders nothing without content, so
 * the section only appears once the agency has added testimonials.
 */
export function TestimonialsSection({ testimonials }: { testimonials: TestimonialItem[] }) {
  const { t } = useLocale();
  const items = testimonials.filter((item) => item.quote.trim() !== '');
  if (items.length === 0) return null;

  return (
    <section className="bg-slate-50 py-10 lg:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('testi.kicker')}
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t('testi.title')}</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.slice(0, 6).map((item, index) => (
            <figure key={index} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-xs">
              <Quote className="h-6 w-6 text-[var(--color-primary)]" />
              <blockquote className="mt-3 grow text-sm leading-relaxed text-slate-600">{item.quote}</blockquote>
              <figcaption className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-[var(--color-primary)]">
                  {item.author.trim().charAt(0).toUpperCase() || '★'}
                </span>
                <span className="text-xs font-semibold text-slate-900">{item.author}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
